import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import {
  Plus,
  Edit,
  Trash2,
  Save,
  X,
  Image as ImageIcon,
  Calendar,
  Tag,
} from 'lucide-react';

interface NewsPost {
  id: string;
  title: string;
  slug: string;
  content: string;
  excerpt: string;
  image_url: string;
  author: string;
  author_image_url: string;
  category: string;
  tags: string[];
  is_featured: boolean;
  published_at: string;
  created_at: string;
}

export default function NewsManager() {
  const { profile } = useAdminAuth();
  const [posts, setPosts] = useState<NewsPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<string | null>(null);
  const [formData, setFormData] = useState<Partial<NewsPost>>({});

  useEffect(() => {
    loadPosts();
  }, []);

  const loadPosts = async () => {
    try {
      const { data, error } = await supabase
        .from('news_posts')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setPosts(data || []);
    } catch (error) {
      console.error('Error loading posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const startNew = () => {
    setFormData({
      title: '',
      slug: '',
      content: '',
      excerpt: '',
      image_url: '',
      author: profile?.display_name || '',
      author_image_url: profile?.avatar_url || '',
      category: 'latest-news',
      tags: [],
      is_featured: false,
    });
    setEditing('new');
  };

  const startEdit = (post: NewsPost) => {
    setFormData(post);
    setEditing(post.id);
  };

  const cancelEdit = () => {
    setEditing(null);
    setFormData({});
  };

  const generateSlug = (title: string) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const handleSave = async () => {
    try {
      const slug = formData.slug || generateSlug(formData.title || '');
      const dataToSave = {
        ...formData,
        slug,
        author: profile?.display_name || formData.author,
        author_image_url: profile?.avatar_url || formData.author_image_url,
      };

      if (editing === 'new') {
        const { error } = await supabase.from('news_posts').insert([dataToSave]);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('news_posts')
          .update(dataToSave)
          .eq('id', editing);
        if (error) throw error;
      }

      await loadPosts();
      cancelEdit();
    } catch (error) {
      console.error('Error saving post:', error);
      alert('Failed to save post');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this post?')) return;

    try {
      const { error } = await supabase.from('news_posts').delete().eq('id', id);
      if (error) throw error;
      await loadPosts();
    } catch (error) {
      console.error('Error deleting post:', error);
      alert('Failed to delete post');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-yellow-400 font-horizon text-2xl animate-pulse">
          Loading News...
        </div>
      </div>
    );
  }

  if (editing) {
    return (
      <div>
        <div className="flex items-center justify-between mb-6">
          <h1 className="font-horizon text-3xl text-yellow-400">
            {editing === 'new' ? 'Create New Post' : 'Edit Post'}
          </h1>
          <button
            onClick={cancelEdit}
            className="flex items-center gap-2 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
          >
            <X size={20} />
            Cancel
          </button>
        </div>

        <div className="bg-gradient-to-br from-red-900/20 to-black border border-yellow-400/30 rounded-xl p-6 space-y-6">
          <div>
            <label className="block text-gray-300 font-bold mb-2">Title *</label>
            <input
              type="text"
              value={formData.title || ''}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-4 py-2 bg-black/50 border border-gray-600 rounded-lg text-white focus:border-yellow-400 focus:outline-none"
              placeholder="Post title"
            />
          </div>

          <div>
            <label className="block text-gray-300 font-bold mb-2">Slug</label>
            <input
              type="text"
              value={formData.slug || ''}
              onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
              className="w-full px-4 py-2 bg-black/50 border border-gray-600 rounded-lg text-white focus:border-yellow-400 focus:outline-none"
              placeholder="auto-generated-from-title"
            />
            <p className="text-gray-500 text-sm mt-1">
              Leave empty to auto-generate from title
            </p>
          </div>

          <div>
            <label className="block text-gray-300 font-bold mb-2">Excerpt</label>
            <textarea
              value={formData.excerpt || ''}
              onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
              className="w-full px-4 py-2 bg-black/50 border border-gray-600 rounded-lg text-white focus:border-yellow-400 focus:outline-none h-24"
              placeholder="Short excerpt for preview"
            />
          </div>

          <div>
            <label className="block text-gray-300 font-bold mb-2">Content *</label>
            <textarea
              value={formData.content || ''}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              className="w-full px-4 py-2 bg-black/50 border border-gray-600 rounded-lg text-white focus:border-yellow-400 focus:outline-none h-64 font-mono"
              placeholder="Full article content (supports markdown)"
            />
          </div>

          <div>
            <label className="block text-gray-300 font-bold mb-2">Featured Image URL</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={formData.image_url || ''}
                onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                className="flex-1 px-4 py-2 bg-black/50 border border-gray-600 rounded-lg text-white focus:border-yellow-400 focus:outline-none"
                placeholder="https://..."
              />
              <button className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2">
                <ImageIcon size={20} />
                Browse
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-gray-300 font-bold mb-2">Category</label>
              <select
                value={formData.category || 'latest-news'}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full px-4 py-2 bg-black/50 border border-gray-600 rounded-lg text-white focus:border-yellow-400 focus:outline-none"
              >
                <option value="latest-news">Latest News</option>
                <option value="game-reviews">Game Reviews</option>
                <option value="tech-ai">Tech & AI</option>
                <option value="resources">Resources</option>
              </select>
            </div>

            <div>
              <label className="block text-gray-300 font-bold mb-2">Tags</label>
              <input
                type="text"
                value={(formData.tags || []).join(', ')}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    tags: e.target.value.split(',').map((t) => t.trim()),
                  })
                }
                className="w-full px-4 py-2 bg-black/50 border border-gray-600 rounded-lg text-white focus:border-yellow-400 focus:outline-none"
                placeholder="tag1, tag2, tag3"
              />
            </div>
          </div>

          <div>
            <label className="flex items-center gap-2 text-gray-300 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.is_featured || false}
                onChange={(e) =>
                  setFormData({ ...formData, is_featured: e.target.checked })
                }
                className="w-5 h-5"
              />
              <span className="font-bold">Featured Post</span>
            </label>
          </div>

          <div className="bg-black/50 border border-gray-600 rounded-lg p-4">
            <h3 className="text-yellow-400 font-bold mb-2">Author Info (Auto-populated)</h3>
            <div className="flex items-center gap-3">
              {profile?.avatar_url && (
                <img
                  src={profile.avatar_url}
                  alt={profile.display_name}
                  className="w-12 h-12 rounded-full object-cover"
                />
              )}
              <div>
                <p className="text-white font-bold">{profile?.display_name}</p>
                <p className="text-gray-400 text-sm">{profile?.bio}</p>
              </div>
            </div>
          </div>

          <div className="flex gap-4">
            <button
              onClick={handleSave}
              className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-yellow-400 text-black font-bold rounded-lg hover:bg-yellow-500 transition-colors"
            >
              <Save size={20} />
              Save Post
            </button>
            <button
              onClick={cancelEdit}
              className="px-6 py-3 bg-gray-700 text-white font-bold rounded-lg hover:bg-gray-600 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-horizon text-3xl text-yellow-400">News & Blog</h1>
        <button
          onClick={startNew}
          className="flex items-center gap-2 px-4 py-2 bg-yellow-400 text-black font-bold rounded-lg hover:bg-yellow-500 transition-colors"
        >
          <Plus size={20} />
          New Post
        </button>
      </div>

      <div className="space-y-4">
        {posts.map((post) => (
          <div
            key={post.id}
            className="bg-gradient-to-br from-red-900/20 to-black border border-yellow-400/30 rounded-xl p-6 hover:border-yellow-400/60 transition-colors"
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="text-white font-bold text-xl">{post.title}</h3>
                  {post.is_featured && (
                    <span className="bg-yellow-400 text-black px-2 py-1 text-xs font-bold rounded">
                      FEATURED
                    </span>
                  )}
                </div>
                <p className="text-gray-400 mb-3">{post.excerpt}</p>
                <div className="flex items-center gap-4 text-sm text-gray-500">
                  <span className="flex items-center gap-1">
                    <Calendar size={14} />
                    {new Date(post.published_at).toLocaleDateString()}
                  </span>
                  <span className="flex items-center gap-1">
                    <Tag size={14} />
                    {post.category}
                  </span>
                  <span>By {post.author}</span>
                </div>
              </div>
              {post.image_url && (
                <img
                  src={post.image_url}
                  alt={post.title}
                  className="w-32 h-32 object-cover rounded-lg"
                />
              )}
            </div>
            <div className="flex gap-2 mt-4">
              <button
                onClick={() => startEdit(post)}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Edit size={16} />
                Edit
              </button>
              <button
                onClick={() => handleDelete(post.id)}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                <Trash2 size={16} />
                Delete
              </button>
            </div>
          </div>
        ))}

        {posts.length === 0 && (
          <div className="text-center py-12 text-gray-400">
            <Newspaper size={48} className="mx-auto mb-4 opacity-50" />
            <p className="text-lg">No posts yet. Create your first post!</p>
          </div>
        )}
      </div>
    </div>
  );
}
